<template>
  <div class="audio-player">
    <product-play
      :class="['audio-player__play-button', { 'is-disabled': elementPrefix === 'footer' && audioPlayerStore.audioErrorMessage }]"
      :reduced-size="elementPrefix === 'footer'"
      :product-details="productDetails"
      :show-text="showText"
      :soundcloud-id="trackId"
      :hide-content="elementPrefix === 'product' && (!trackId || (lastCheckedTrackId === trackId && isTrackAvailable === false))"
      :on-toggle="elementPrefix === 'product' ? handleProductToggle : undefined" />
    <template v-if="elementPrefix === 'footer'">
      <div v-if="audioSrc" :id="playerElementId" :class="['audio-player__waveform', { 'is-ready': waveIsReady }]" />
      <div v-else-if="!isLoading && audioPlayerStore.audioErrorMessage" class="audio-player__error">
        {{ audioPlayerStore.audioErrorMessage }}
      </div>
      <div v-if="audioSrc && showTrackLength && !audioPlayerStore.audioErrorMessage" class="audio-player__time">
        {{ playbackTime }} / {{ duration }}
      </div>
    </template>
  </div>
</template>

<script>
import { useAudioPlayerStore } from "~/stores/audioplayer";
import { formatTime } from "~/utils/audioplayer";
import { nextTick } from 'vue'
import WaveSurfer from 'wavesurfer.js';

export default {
  props: {
    elementPrefix: {
      type: String,
      required: true,
    },
    trackId: String,
    autoPlay: Boolean,
    showTrackLength: {
      type: Boolean,
      default: true,
    },
    productDetails: Object,
    showText: Boolean,
  },
  setup() {
    const audioPlayerStore = useAudioPlayerStore()

    return { audioPlayerStore }
  },
  data() {
    return {
      audioSrc: '',
      displayWaveSurfer: null,
      playerElementId: `${this.elementPrefix}-waveformBar`,
      playbackTime: '0:00',
      duration: '0:00',
      waveIsReady: false,
      isTrackAvailable: null,
      lastCheckedTrackId: null,
      isLoading: this.elementPrefix === 'footer',
    }
  },
  mounted() {
    if (this.elementPrefix === 'footer' && this.trackId) {
      this.fetchTrack();
    }
    if (this.elementPrefix === 'product' && this.trackId) {
      this.checkAvailability();
    }
  },
  beforeDestroy() {
    if (this.displayWaveSurfer) {
      this.displayWaveSurfer.destroy()
    }
  },
  computed: {
    loadedWaveSurfer() {
      return this.audioPlayerStore.loadedWaveSurfer;
    }
  },
  watch: {
    trackId(newId, oldId) {
      if (newId && newId !== oldId) {
        if (this.elementPrefix === 'footer') {
          this.isLoading = true
          this.fetchTrack();
        } else if (this.elementPrefix === 'product') {
          this.isTrackAvailable = null;
          this.checkAvailability();
        }
      }
    },
    '$route.fullPath'(to, from) {
      if (this.elementPrefix === 'product') {
        this.isTrackAvailable = null
        this.lastCheckedTrackId = null
        if (this.trackId) {
          this.checkAvailability()
        }
      }
    }
  },
  methods: {
    async checkAvailability() {
      const currentId = this.trackId
      const available = await this.audioPlayerStore.checkTrackAvailability(currentId)
      if (this.trackId === currentId) {
        this.isTrackAvailable = available
        this.lastCheckedTrackId = currentId
      }
    },
    handleProductToggle() {
      this.isTrackAvailable = null
      this.lastCheckedTrackId = null
      if (! this.audioPlayerStore?.loadedProductDetails
        || (this.audioPlayerStore.loadedProductDetails.soundcloudId !== this.productDetails?.soundcloudId)) {
        this.audioPlayerStore.loadFooterAudio(this.productDetails, true)
        return
      }
      this.audioPlayerStore.togglePlay()
    },
    async fetchTrack() {
      if (this.elementPrefix !== 'footer') {
        return;
      }
      this.isLoading = true
      this.audioSrc = await this.audioPlayerStore.fetchTrackWithHandling(this.trackId, this.productDetails)

      if (this.audioSrc) {
        this.initWaveSurfer()
      } else {
        if (this.displayWaveSurfer) {
          this.displayWaveSurfer.destroy()
          this.displayWaveSurfer = null
        }
      }
      this.isLoading = false
    },
    async initWaveSurfer() {
      if (this.autoPlay) {
        this.audioPlayerStore.destroyLoadedWaveSurfer()
      }

      await nextTick()

      this.displayWaveSurfer = WaveSurfer.create({
        container: `#${this.playerElementId}`,
        waveColor: '#000000',
        progressColor: '#ef860c',
        height: 42,
        response: true,
        barWidth: 3,
        barGap: 2,
        barRadius: 10,
        cursorColor: '#ef860c',
      })

      this.displayWaveSurfer.load(this.audioSrc)

      this.displayWaveSurfer.on('finish', () => {
        this.audioPlayerStore.isPlaying = false
      })

      this.displayWaveSurfer.on('ready', () => {
        /* Working Cantrip for Debugging the Wavesurfer Ready Event */
        /**
         console.log('XY - WAVESURFER READY:', {
         shouldPlay: this.audioPlayerStore.shouldPlay,
         isPlaying: this.audioPlayerStore.isPlaying,
         triggeredByScroll: this.audioPlayerStore.triggeredByScroll,
         userClickedPlay: this.audioPlayerStore.userClickedPlay
         });
         **/
        this.waveIsReady = true
        this.audioPlayerStore.setLoadedWaveSurfer(this.displayWaveSurfer)
        if (this.audioPlayerStore.shouldPlay) {
          this.audioPlayerStore.togglePlay(true)
        }
      })

      if (this.showTrackLength) {
        this.displayWaveSurfer.on('decode', (duration) => {
          this.duration = formatTime(duration)
        })

        this.displayWaveSurfer.on('timeupdate', (currentTime) => {
          this.playbackTime = formatTime(currentTime)
        })
      }
    },
    togglePlay(setPlaying = false) {
      if (!this.displayWaveSurfer) {
        this.audioPlayerStore.loadFooterAudio(this.productDetails, true)
        this.fetchTrack()
        return
      }

      if (!this.loadedWaveSurfer || (this.loadedWaveSurfer.media.src !== this.displayWaveSurfer.media.src)) {
        this.audioPlayerStore.loadFooterAudio(this.productDetails, true)
        this.audioPlayerStore.setLoadedWaveSurfer(this.displayWaveSurfer)

        if (this.waveIsReady) {
          this.audioPlayerStore.togglePlay(true)
        } else {
          this.displayWaveSurfer.on('ready', () => this.audioPlayerStore.togglePlay(true))
        }
        return
      }

      this.audioPlayerStore.togglePlay(setPlaying)
    }
  }
}
</script>

<style lang="scss">
@use "~/common/scss/variables";
@use "~/common/scss/helpers";
@use "~/common/scss/mq";

.audio-player {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;

  &__waveform {
    width: 100%;
    height: 42px;
    display: block;
    visibility: hidden;
    &.is-ready {
      visibility: visible;
    }
  }

  @include mq.mobileOnly() {
    &__waveform {
      display: none;
    }
  }

  &__play-button {
    cursor: pointer;
  }

  &__time {
    min-width: 6rem;
    text-align: center;
    white-space: nowrap;
  }

  &__error {
    color: #e6421a;
    font-size: helpers.px-to-rem(18);
    width: variables.$containerSmall;
    text-align: center;

    @include mq.tabletOnly() {
      width: variables.$containerSmall;
      font-size: helpers.px-to-rem(16);
    }

    @include mq.mobileOnly() {
      width: 100%;
      margin: 0 0 0 1.5rem;
      font-size: helpers.px-to-rem(14);
    }
  }
}
</style>
